import { genericDelete, genericFetch, genericPatch, genericPost } from './api';
import { Auth } from '../state/auth/types';
import { AxiosInstance, AxiosResponse } from 'axios';
import { CarrierEnum, ShipmentTypeEnum } from '../state/shipments/types';
import {
  AddressEntity,
  NewOrExistingAddressEntity,
} from '../state/addresses/types';
import { OrderEntity, OrderStatus } from '../state/orders/types';
import { DeviceType } from '../state/devices/types';
import {
  CarrierTrackingSnapshot,
  ShipmentEntity,
} from '../state/shipments/types';

const ARTYC_ORDER_URL = '/artyc/orders';
const companyOrdersUrl = (auth: Auth | null) =>
  `/api/v1/company/${auth?.companyId}/orders`;

export interface ShipmentBody {
  leg: number;
  shipmentType: ShipmentTypeEnum;
  carrier?: CarrierEnum;
  trackingNumber?: string;
  origin?: NewOrExistingAddressEntity;
  destination?: NewOrExistingAddressEntity;
}

export interface CreateCaasOrderRequestBody {
  company: string;
  shippingProfile: string;
  expectedDeliveryDate?: string;
  poNumber?: string;
  customId?: string;
  device?: string;
  shipments: ShipmentBody[];
}

export type UpdateOrderShipmentBody = Omit<
  ShipmentBody,
  'leg' | 'shipmentType' | 'origin' | 'destination'
> & {
  destination: Omit<AddressEntity, 'country' | 'addressType'>;
};

export interface UpdateCaasOrderRequestBody {
  expectedDeliveryDate?: string;
  device?: string;
  shipments: {
    [shipmentId: string]: UpdateOrderShipmentBody;
  };
}

export interface CreateLeaseOrderRequestBody {
  company: string;
  expectedDeliveryDate?: string;
  poNumber?: string;
  customId?: string;
  shipment: ShipmentBody;
  shipperType: DeviceType;
  quantity: number;
  sku?: string;
}

export type UpdateLeaseOrderRequestBody = Omit<
  CreateLeaseOrderRequestBody,
  'company' | 'shipment'
> & {
  shipment?: UpdateOrderShipmentBody;
};

export interface FulfillLeaseOrderRequestBody {
  shippers: { device: string }[];
}

const OrdersApi = {
  getOrder: async (
    axios: AxiosInstance,
    auth: Auth | null,
    orderId: string
  ): Promise<OrderEntity> => {
    const response: OrderEntity = await genericFetch(
      axios,
      auth,
      `${ARTYC_ORDER_URL}/${orderId}`,
      `${companyOrdersUrl(auth)}/${orderId}`
    );
    return response;
  },
  getShipmentForOrder: async (
    axios: AxiosInstance,
    auth: Auth | null,
    orderId: string
  ): Promise<ShipmentEntity> => {
    const response: ShipmentEntity = await genericFetch(
      axios,
      auth,
      `${ARTYC_ORDER_URL}/${orderId}/shipment`,
      `${companyOrdersUrl(auth)}/${orderId}/shipment`
    );
    return response;
  },
  createCaasOrder: async (
    axios: AxiosInstance,
    auth: Auth | null,
    orderData: CreateCaasOrderRequestBody
  ): Promise<{ journeyId: string; orderId: string }> => {
    const response: AxiosResponse<{ journeyId: string; orderId: string }> =
      await genericPost(
        axios,
        auth,
        `${ARTYC_ORDER_URL}/caas`,
        `${companyOrdersUrl(auth)}/caas`,
        orderData
      );

    return response.data;
  },
  updateCaasOrder: async (
    axios: AxiosInstance,
    auth: Auth | null,
    orderId: string,
    orderUpdate: UpdateCaasOrderRequestBody
  ): Promise<{ journeyId: string; orderId: string }> => {
    const response: AxiosResponse<{ journeyId: string; orderId: string }> =
      await genericPatch(
        axios,
        auth,
        `${ARTYC_ORDER_URL}/caas/${orderId}`,
        `${companyOrdersUrl(auth)}/caas/${orderId}`,
        orderUpdate
      );
    return response.data;
  },
  updateOrderStatus: async (
    axios: AxiosInstance,
    auth: Auth | null,
    orderId: string,
    status: OrderStatus
  ): Promise<void> => {
    return genericPatch(
      axios,
      auth,
      `${ARTYC_ORDER_URL}/${orderId}/status`,
      `${companyOrdersUrl(auth)}/${orderId}/status`,
      {
        status,
      }
    );
  },
  createLeaseOrder: async (
    axios: AxiosInstance,
    auth: Auth | null,
    orderData: CreateLeaseOrderRequestBody
  ): Promise<{ orderId: string }> => {
    const response: AxiosResponse<{ orderId: string }> = await genericPost(
      axios,
      auth,
      `${ARTYC_ORDER_URL}/lease`,
      `${companyOrdersUrl(auth)}/lease`,
      orderData
    );

    return response.data;
  },
  fulfillLeaseOrder: async (
    axios: AxiosInstance,
    auth: Auth | null,
    orderId: string,
    orderUpdate: FulfillLeaseOrderRequestBody
  ): Promise<{ orderId: string }> => {
    const response: AxiosResponse<{ orderId: string }> = await genericPatch(
      axios,
      auth,
      `${ARTYC_ORDER_URL}/lease/${orderId}/fulfill`,
      `${companyOrdersUrl(auth)}/lease/${orderId}/fulfill`,
      orderUpdate
    );
    return response.data;
  },
  deleteOrder: async (
    axios: AxiosInstance,
    auth: Auth | null,
    orderId: string
  ): Promise<void> => {
    await genericDelete(
      axios,
      auth,
      `${ARTYC_ORDER_URL}/${orderId}`,
      `${companyOrdersUrl(auth)}/${orderId}`
    );
  },
  updateShipmentTracking: async (
    axios: AxiosInstance,
    auth: Auth | null,
    shipmentId: string,
    carrier: CarrierEnum,
    trackingNumber: string
  ): Promise<void> => {
    await genericPatch(
      axios,
      auth,
      `${ARTYC_ORDER_URL}/shipments/${shipmentId}/tracking`,
      `${companyOrdersUrl(auth)}/shipments/${shipmentId}/tracking`,
      { carrier, trackingNumber }
    );
  },
  removeShipmentTracking: async (
    axios: AxiosInstance,
    auth: Auth | null,
    shipmentId: string
  ): Promise<void> => {
    await genericDelete(
      axios,
      auth,
      `${ARTYC_ORDER_URL}/shipments/${shipmentId}/tracking`,
      `${companyOrdersUrl(auth)}/shipments/${shipmentId}/tracking`
    );
  },
  getShipmentLiveTracking: async (
    axios: AxiosInstance,
    auth: Auth | null,
    shipmentId: string
  ): Promise<CarrierTrackingSnapshot> => {
    return genericFetch<CarrierTrackingSnapshot>(
      axios,
      auth,
      `${ARTYC_ORDER_URL}/shipments/${shipmentId}/tracking/live`,
      `${companyOrdersUrl(auth)}/shipments/${shipmentId}/tracking/live`
    );
  },
};

export default OrdersApi;
